// frontend/src/lib/breakpoints.ts
// Every breakpoint the JavaScript side asks about, named once.
//
// This started as the chamber's three values and has been widened to the whole
// app, because the drift it was written to stop was not confined to the
// chamber. "Is this a phone?" was being answered in six places with five
// different numbers: 767 in the header, 760 in the mobile notice, 768 in
// The Court, About and Summary, 860 in Briefs and 900 in the Docket. Three of
// those are the same question with answers 8px apart, which is not a design
// decision, it is a copy of a copy.
//
// The earlier instance of the same thing: BenchView chose the mobile bench at
// `max-width: 767px` while every CSS rule building the mobile argument column
// keyed off 768px, so at exactly 768px, a common tablet width, the desktop
// bench rendered inside the mobile layout.
//
// The rule that keeps this fixed: a width literal never appears inside a
// matchMedia call. It comes from here, or it is a new breakpoint and belongs
// here first. breakpoints.test.ts enforces that.
//
// ---------------------------------------------------------------------------
// THE CSS SIDE MUST MOVE WITH THESE. A custom property cannot be read inside a
// media query, so the stylesheet cannot import them and there is no build step
// here that could substitute them. Changing a value below means changing its
// partner in `public/colors_and_type.css` in the same commit:
//
//   MOBILE_MAX (768)     @media (max-width: 768px)              the mobile column
//                        @media (max-width: 768px), (max-height: 640px)
//                        @media (min-width: 769px)              the one-viewport shell
//   SHORT_MAX (900)      @media (min-width: 769px) and (max-height: 900px)
//   VERY_SHORT_MAX (820) @media (min-width: 769px) and (max-height: 820px)
//
// The `min-width: 769px` rules are the complement of MOBILE_MAX, so they are
// MOBILE_MAX + 1 and move with it. breakpoints.test.ts checks that the
// stylesheet still contains the partners listed here, so this comment cannot
// quietly stop being true.
// ---------------------------------------------------------------------------

/**
 * At or below this width the layout is the phone column. This is the app's
 * one answer to "is this a phone", and every screen that only needs to know
 * that should use it rather than picking a nearby number.
 */
export const MOBILE_MAX = 768;

/**
 * The Docket stacks its ledger and its brief below this. Deliberately NOT
 * MOBILE_MAX: this is "do two columns of case rows and a brief still fit",
 * which a small tablet fails while still being a perfectly good phone-column
 * exception. Same digits as SHORT_MAX by coincidence, different axis.
 */
export const DOCKET_STACK_MAX = 900;

/**
 * The brief stacks its columns below this. Again not MOBILE_MAX: the brief's
 * measure runs out earlier than the Docket's because its second column is
 * wider.
 */
export const BRIEF_STACK_MAX = 860;

/**
 * At or below this width the header's six-link ledger nav collapses into the
 * menu trigger the phone already uses. Deliberately NOT MOBILE_MAX: this is
 * "do six uppercase nav links, the wordmark and the two controls fit inside
 * the header's 1240px column", which stops being true at 1163px, far above
 * any phone. 1200 is the nearest round number that leaves the row real slack
 * rather than a width some device happens to have.
 */
export const LEDGER_NAV_COLLAPSE_MAX = 1200;

/** At or below this height the room compacts: smaller bench, no caption subtitle. */
export const SHORT_MAX = 900;

/** At or below this height the lectern disc and the transcript caption go. */
export const VERY_SHORT_MAX = 820;

export const MQ_MOBILE = `(max-width: ${MOBILE_MAX}px)`;
export const MQ_DOCKET_STACK = `(max-width: ${DOCKET_STACK_MAX}px)`;
export const MQ_BRIEF_STACK = `(max-width: ${BRIEF_STACK_MAX}px)`;
export const MQ_LEDGER_NAV_COLLAPSED = `(max-width: ${LEDGER_NAV_COLLAPSE_MAX}px)`;
export const MQ_SHORT = `(max-height: ${SHORT_MAX}px)`;
export const MQ_VERY_SHORT = `(max-height: ${VERY_SHORT_MAX}px)`;

/** Below this height a phone's locker (Settings) shows one page at a time: door, dials, name, keys. */
export const LOCKER_PAGED_MAX_H = 959;
export const MQ_LOCKER_PAGED = `(max-width: ${MOBILE_MAX}px) and (max-height: ${LOCKER_PAGED_MAX_H}px)`;
